import React, { useState } from 'react';
import styled from 'styled-components';
import { images } from '../../../assets/images';
import { isValidEmail } from '../../../utils/password-policy';
import { AuthCard, AuthInner, AuthLogo, AuthTitle, AuthLink, AuthInput, AuthSubmitButton, ErrorBanner } from './AuthUi';

export interface ForgotPasswordScreenProps {
  onReset: (email: string) => void;
  onBackToLogin: () => void;
  loading: boolean;
  errorMessage?: string | null;
  sent: boolean;
  socialProvider: 'google' | 'apple' | null;
}

export const ForgotPasswordScreen = (props: ForgotPasswordScreenProps) => {
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);

  const emailError = touched && !isValidEmail(email) ? 'Enter a valid email.' : null;
  const providerLabel = props.socialProvider === 'google' ? 'Google' : props.socialProvider === 'apple' ? 'Apple' : '';

  const submit = () => {
    setTouched(true);
    if (!isValidEmail(email)) return;
    props.onReset(email.trim());
  };

  return (
    <AuthCard>
      <AuthInner>
        <AuthLogo src={images.risenHopeLogo} alt="Risen Hope" />
        <AuthTitle>Reset your password</AuthTitle>

        {props.socialProvider ? (
          <>
            <SubText>
              This email is registered with {providerLabel}. Please go back and tap "Continue with {providerLabel}" to
              sign in.
            </SubText>
            <Spacer $size={28} />
            <AuthSubmitButton title="Back to login" onClick={props.onBackToLogin} />
          </>
        ) : (
          <>
            {props.sent ? (
              <>
                <SubText>
                  If an account exists for{email ? ` ${email}` : ' that email'}, we've sent a link to reset your
                  password. Check your inbox and spam folder.
                </SubText>
                <Spacer $size={8} />
                <SubText>Didn't receive email? You can resend again.</SubText>
              </>
            ) : (
              <SubText>Enter your email and we'll send you a reset link.</SubText>
            )}
            <Spacer $size={24} />

            <AuthInput
              label="Email"
              value={email}
              onChange={setEmail}
              placeholder="you@example.com"
              type="email"
              error={emailError}
              onKeyDown={e => {
                if (e.key === 'Enter') submit();
              }}
            />

            {!!props.errorMessage && (
              <>
                <Spacer />
                <ErrorBanner message={props.errorMessage} />
              </>
            )}

            <Spacer $size={24} />
            <AuthSubmitButton
              title={props.sent ? 'Resend reset link' : 'Send reset link'}
              onClick={submit}
              loading={props.loading}
            />
            <Spacer $size={24} />
            <BackRow>
              <AuthLink onClick={props.onBackToLogin}>Back to login</AuthLink>
            </BackRow>
          </>
        )}
      </AuthInner>
    </AuthCard>
  );
};

const Spacer = styled.div<{ $size?: number }>`
  height: ${p => p.$size ?? 16}px;
`;

const SubText = styled.p`
  font-family: Nunito, sans-serif;
  font-size: 14px;
  color: #8a9099;
  line-height: 22px;
  margin: 0;
`;

const BackRow = styled.div`
  display: flex;
  justify-content: center;
`;
